import type { LucideIconName } from 'components/LucideIcon'
import {
  COMMENTS_CATEGORY,
  CREATOR_CATEGORY,
  SIGNOFFS_ID,
  STAGE_CATEGORY,
  VERSION_CATEGORY,
} from 'features/libra/dashboard/ticketDashboard/libra/pills/categoryIds'
import { LIST_VIEW_ID } from 'features/libra/dashboard/ticketDashboard/types'
import { icons } from 'lucide-react'
import z from 'zod'

/** Built-in category ids, plus the list view's id since a field pill's id can name a board. */
const RESERVED_IDS = new Set([
  LIST_VIEW_ID,
  STAGE_CATEGORY,
  VERSION_CATEGORY,
  CREATOR_CATEGORY,
  SIGNOFFS_ID,
  COMMENTS_CATEGORY,
])

const pillToneSchema = z.union([
  z.enum(['default', 'processing', 'success', 'warning', 'error']),
  z.object({ color: z.string() }),
])

/** A Lucide icon name, e.g. `CircleCheck`; see lucide.dev/icons. */
const pillIconSchema = z.custom<LucideIconName>(
  (value) => typeof value === 'string' && Object.hasOwn(icons, value),
  'Not a Lucide icon name'
)

/** Dot path, e.g. `eligibility` or `components.shell.grade`. */
const fieldPathSchema = z.string().regex(/^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*$/)

const fieldPillSchema = z.object({
  id: z
    .string()
    .regex(/^[a-z][a-zA-Z0-9_]*$/)
    .refine((id) => !RESERVED_IDS.has(id), 'Id is reserved'),
  label: z.string(),
  source: z.enum(['fields', 'ticket']).optional(),
  path: fieldPathSchema,
  format: z.enum(['user', 'date']).optional(),
  scope: z.enum(['asset', 'ticket']).default('asset'),
  icon: pillIconSchema.optional(),
  empty: z.string().optional(),
  description: z.string().optional(),
  values: z
    .array(
      z.object({
        value: z.string(),
        label: z.string().optional(),
        tone: pillToneSchema.optional(),
        icon: pillIconSchema.optional(),
        description: z.string().optional(),
      })
    )
    .default([]),
  filter: z.boolean().optional(),
  showOnlyOnModal: z.boolean().optional(),
})

export type FieldPillSettings = z.infer<typeof fieldPillSchema>

const builtInPillSchema = z.object({
  enabled: z.boolean().default(true),
  filter: z.boolean().optional(),
})

export type BuiltInPillSettings = z.infer<typeof builtInPillSchema>

const signoffPillsSchema = builtInPillSchema.extend({
  stages: z.array(z.string()).optional(),
})

const boardSettingsSchema = z.union([
  z.string().transform((id) => ({ id, otherColumn: undefined })),
  z.object({
    id: z.string(),
    otherColumn: z.string().optional(),
  }),
])

export type BoardSettings = z.infer<typeof boardSettingsSchema>

/** Documented under "Ticket Dashboard" in `features/libra/README.md`. */
const ticketDashboardSettingsObjectSchema = z.object({
  builtIns: z
    .object({
      stage: builtInPillSchema.prefault({}),
      version: builtInPillSchema.prefault({}),
      creator: builtInPillSchema.prefault({}),
      signoffs: signoffPillsSchema.prefault({}),
      comments: builtInPillSchema.prefault({}),
    })
    .prefault({}),
  fields: z.array(fieldPillSchema).default([]),
  boards: z.array(boardSettingsSchema).optional(),
  /** Roles besides admins; like a lifecycle action's `roles`, an empty list allows anyone. */
  createNewVersion: z
    .object({ roles: z.array(z.string()).optional() })
    .prefault({}),
})

export type TicketDashboardSettings = z.infer<
  typeof ticketDashboardSettingsObjectSchema
>

export const DEFAULT_TICKET_DASHBOARD_SETTINGS: TicketDashboardSettings =
  ticketDashboardSettingsObjectSchema.parse({})

/** A broken block falls back to the defaults rather than failing the whole dashboard config. */
export const ticketDashboardSettingsSchema =
  ticketDashboardSettingsObjectSchema.catch(({ error }) => {
    console.warn(
      '[Libra] Ignoring invalid ticket dashboard settings:',
      error.issues
    )
    return DEFAULT_TICKET_DASHBOARD_SETTINGS
  })
